/**
 * Media-engine shims (Fields/Links P2, migration 0024).
 *
 * VideoPlayer is built around a native <video> element it reads through a ref:
 * currentTime, duration, paused, volume, muted, playbackRate, loop, play(),
 * pause(), and the play/pause/timeupdate/durationchange/volumechange/ratechange
 * events. To play YouTube / Vimeo links WITHOUT rewriting the player, we wrap
 * each vendor SDK in an object that exposes that exact surface — a "shim" that
 * quacks like an HTMLVideoElement. The player then treats a link identically to
 * an upload, so every overlay, the annotation canvas and the control bar work
 * unchanged.
 *
 * Reads are served from a cached state object (updated by the SDK's events / a
 * poll) so the player's SYNCHRONOUS `el.currentTime` reads always work — Vimeo's
 * native getters are async, and YouTube has no timeupdate event.
 */

/** The subset of HTMLVideoElement the player touches. HTMLVideoElement also
 *  satisfies this structurally, so `videoRef.current` can hold either. */
export type MediaLike = {
  currentTime: number;
  readonly duration: number;
  readonly paused: boolean;
  volume: number;
  muted: boolean;
  playbackRate: number;
  loop: boolean;
  play: () => Promise<void>;
  pause: () => void;
  addEventListener: (type: string, cb: () => void) => void;
  removeEventListener: (type: string, cb: () => void) => void;
  /** Not on HTMLVideoElement — present only on shims. Tears down SDK + timers. */
  destroy?: () => void;
};

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    YT?: any;
    onYouTubeIframeAPIReady?: () => void;
    Vimeo?: any;
  }
}

const scriptPromises = new Map<string, Promise<void>>();
function loadScript(src: string): Promise<void> {
  const existing = scriptPromises.get(src);
  if (existing) return existing;
  const p = new Promise<void>((resolve, reject) => {
    const el = document.createElement("script");
    el.src = src;
    el.async = true;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(el);
  });
  scriptPromises.set(src, p);
  return p;
}

let ytReadyPromise: Promise<any> | null = null;
function ensureYouTubeApi(): Promise<any> {
  if (typeof window !== "undefined" && window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (ytReadyPromise) return ytReadyPromise;
  ytReadyPromise = new Promise((resolve) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev?.();
      resolve(window.YT);
    };
    void loadScript("https://www.youtube.com/iframe_api");
  });
  return ytReadyPromise;
}

/** Build a YouTube shim. `mount` is a throwaway <div> the API replaces with an iframe. */
export async function createYouTubeShim(mount: HTMLElement, videoId: string): Promise<MediaLike> {
  const YT = await ensureYouTubeApi();
  const et = new EventTarget();
  const state = { time: 0, duration: 0, paused: true, volume: 1, muted: false, rate: 1, loop: false };
  let poll: number | null = null;
  let player: any;

  await new Promise<void>((resolve) => {
    player = new YT.Player(mount, {
      videoId,
      width: "100%",
      height: "100%",
      playerVars: {
        controls: 0, modestbranding: 1, rel: 0, playsinline: 1,
        disablekb: 1, fs: 0, iv_load_policy: 3,
      },
      events: {
        onReady: () => {
          state.duration = player.getDuration() || 0;
          try { state.volume = (player.getVolume() ?? 100) / 100; state.muted = !!player.isMuted(); } catch { /* noop */ }
          et.dispatchEvent(new Event("durationchange"));
          resolve();
        },
        onStateChange: (e: any) => {
          if (e.data === 1) { state.paused = false; et.dispatchEvent(new Event("play")); }
          else if (e.data === 2) { state.paused = true; et.dispatchEvent(new Event("pause")); }
          else if (e.data === 0) {
            state.paused = true;
            if (state.loop) { player.seekTo(0, true); player.playVideo(); }
            else et.dispatchEvent(new Event("pause"));
          }
        },
      },
    });
  });

  poll = window.setInterval(() => {
    try {
      const t = player.getCurrentTime() ?? 0;
      if (t !== state.time) { state.time = t; et.dispatchEvent(new Event("timeupdate")); }
      const d = player.getDuration() ?? 0;
      if (d && d !== state.duration) { state.duration = d; et.dispatchEvent(new Event("durationchange")); }
    } catch { /* player torn down */ }
  }, 150);

  return {
    get currentTime() { return state.time; },
    set currentTime(v: number) { state.time = v; try { player.seekTo(v, true); } catch { /* noop */ } et.dispatchEvent(new Event("timeupdate")); },
    get duration() { return state.duration; },
    get paused() { return state.paused; },
    get volume() { return state.volume; },
    set volume(v: number) { state.volume = v; try { player.setVolume(Math.round(v * 100)); } catch { /* noop */ } et.dispatchEvent(new Event("volumechange")); },
    get muted() { return state.muted; },
    set muted(m: boolean) { state.muted = m; try { m ? player.mute() : player.unMute(); } catch { /* noop */ } et.dispatchEvent(new Event("volumechange")); },
    get playbackRate() { return state.rate; },
    set playbackRate(r: number) { state.rate = r; try { player.setPlaybackRate(r); } catch { /* noop */ } et.dispatchEvent(new Event("ratechange")); },
    get loop() { return state.loop; },
    set loop(l: boolean) { state.loop = l; },
    play() { try { player.playVideo(); } catch { /* noop */ } return Promise.resolve(); },
    pause() { try { player.pauseVideo(); } catch { /* noop */ } },
    addEventListener: (t, cb) => et.addEventListener(t, cb),
    removeEventListener: (t, cb) => et.removeEventListener(t, cb),
    destroy() { if (poll) window.clearInterval(poll); try { player.destroy(); } catch { /* noop */ } },
  };
}

/** Build a Vimeo shim. `mount` hosts the iframe. `url` carries any unlisted hash. */
export async function createVimeoShim(mount: HTMLElement, url: string): Promise<MediaLike> {
  await loadScript("https://player.vimeo.com/api/player.js");
  const Vimeo = window.Vimeo;
  const et = new EventTarget();
  const state = { time: 0, duration: 0, paused: true, volume: 1, muted: false, rate: 1, loop: false };
  let preMuteVolume = 1;

  const player = new Vimeo.Player(mount, { url, controls: false, autoplay: false, playsinline: true });
  await player.ready();
  try { state.duration = await player.getDuration(); et.dispatchEvent(new Event("durationchange")); } catch { /* noop */ }

  player.on("timeupdate", (d: any) => {
    state.time = d.seconds;
    if (d.duration) state.duration = d.duration;
    et.dispatchEvent(new Event("timeupdate"));
  });
  player.on("durationchange", (d: any) => { state.duration = d.duration; et.dispatchEvent(new Event("durationchange")); });
  player.on("play", () => { state.paused = false; et.dispatchEvent(new Event("play")); });
  player.on("pause", () => { state.paused = true; et.dispatchEvent(new Event("pause")); });
  player.on("ended", () => {
    state.paused = true;
    if (state.loop) { player.setCurrentTime(0); player.play(); }
    else et.dispatchEvent(new Event("pause"));
  });
  player.on("playbackratechange", (d: any) => { state.rate = d.playbackRate; et.dispatchEvent(new Event("ratechange")); });
  player.on("volumechange", (d: any) => {
    state.volume = d.volume;
    state.muted = d.volume === 0;
    if (d.volume > 0) preMuteVolume = d.volume;
    et.dispatchEvent(new Event("volumechange"));
  });

  return {
    get currentTime() { return state.time; },
    set currentTime(v: number) { state.time = v; player.setCurrentTime(v).catch(() => {}); },
    get duration() { return state.duration; },
    get paused() { return state.paused; },
    get volume() { return state.volume; },
    set volume(v: number) { state.volume = v; if (v > 0) preMuteVolume = v; player.setVolume(v).catch(() => {}); },
    get muted() { return state.muted; },
    set muted(m: boolean) { state.muted = m; player.setVolume(m ? 0 : (preMuteVolume || 0.5)).catch(() => {}); },
    get playbackRate() { return state.rate; },
    set playbackRate(r: number) { state.rate = r; player.setPlaybackRate(r).catch(() => {}); },
    get loop() { return state.loop; },
    set loop(l: boolean) { state.loop = l; },
    play() { return player.play().catch(() => {}); },
    pause() { player.pause().catch(() => {}); },
    addEventListener: (t, cb) => et.addEventListener(t, cb),
    removeEventListener: (t, cb) => et.removeEventListener(t, cb),
    destroy() { try { player.destroy(); } catch { /* noop */ } },
  };
}
